/**
 * @file trackerDiscovery.js
 * @description One-time "discovery" nudge that surfaces the free Pay & Hours
 *   tracker to therapists who haven't found it yet. It drops a single
 *   notification into the bell ("Pay & Hours is in your profile menu") that,
 *   when clicked, opens the tracker.
 *
 *   Safety / once-only guarantees, layered:
 *     1. NEW_ACCOUNT_QUIET_MS — nothing fires in an account's first day. See
 *        below for why.
 *     2. localStorage attempt-guard — avoids re-running the check every render
 *        on a given device.
 *     3. Skip if the therapist has already used the tracker (has a consent row).
 *     4. dedupe_key `tracker_discovery:<uid>` — the DB/service de-dupes so even
 *        across devices it can only ever create the row once.
 *
 *   ── Why the quiet window ────────────────────────────────────────────────
 *   This used to fire the moment onboarding flipped `onboarded`. One RBT got
 *   it five minutes after signing up, in the same SECOND as the "complete your
 *   profile" nudge, then a credential email two hours later — three touches on
 *   day one, before she had done anything. She complained about the volume.
 *
 *   A feature tip is not urgent. It is worth nothing on day zero, when the
 *   person is still forming a first impression and has not yet had a reason to
 *   want a pay tracker. Holding it a day costs the tip nothing and keeps the
 *   signup hour for things the user actually asked for (verify your email,
 *   your credential was approved).
 *
 *   Title/body are passed in by the caller so this stays i18n-agnostic (the
 *   caller has the live `t`).
 *
 * @module lib/trackerDiscovery
 */
import { createNotification } from "./notificationService";
import { loadSettings, hasConsented } from "./timesheetService";

const lsKey = (userId) => `tracker:discoveryNotified:${userId}`;

/**
 * How long after signup non-urgent product announcements stay silent.
 * send-platform-update/index.ts enforces the same window for admin broadcasts
 * — it is a Deno edge function and cannot import from src/, so the two carry
 * the same constant by hand. Change both together.
 */
export const NEW_ACCOUNT_QUIET_MS = 24 * 60 * 60 * 1000;

/**
 * Create the discovery notification once, if appropriate. Best-effort: any
 * failure is swallowed (this is a non-critical convenience nudge).
 *
 * @param {string} userId
 * @param {{title:string, body:string, accountCreatedAt?:string|Date}} copy
 *   localized strings from the caller, plus the account's creation timestamp
 *   for the quiet-window check. A missing/unparseable timestamp does NOT
 *   suppress the tip — an unknown age is treated as an established account,
 *   because silently losing the tip forever is worse than sending it a little
 *   early.
 */
export async function maybeNotifyTrackerDiscovery(userId, { title, body, accountCreatedAt } = {}) {
  if (!userId || !title) return;

  // Quiet window — see the file header.
  if (accountCreatedAt) {
    const created = new Date(accountCreatedAt).getTime();
    if (Number.isFinite(created) && Date.now() - created < NEW_ACCOUNT_QUIET_MS) return;
  }

  try {
    if (localStorage.getItem(lsKey(userId))) return;
  } catch { /* storage blocked — fall through, dedupe still protects us */ }

  try {
    // Already a tracker user? Then they've found it — don't nag.
    const settings = await loadSettings(userId);
    if (hasConsented(settings)) {
      try { localStorage.setItem(lsKey(userId), "1"); } catch { /* ignore */ }
      return;
    }

    await createNotification({
      user_id: userId,
      type: "tracker_discovery",
      title,
      body,
      link_data: { view: "tracker" },
      dedupe_key: `tracker_discovery:${userId}`,
    });

    try { localStorage.setItem(lsKey(userId), "1"); } catch { /* ignore */ }
  } catch (e) {
    // Non-critical — never block the app on a discovery tip.
    console.error("[Tracker] discovery notify failed", e);
  }
}
